import { Directive, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output, inject } from '@angular/core';

/**
 * Emits once, the first time the host element scrolls into view.
 *
 * Exists so per-row work that costs a network request — contact photos, which Graph serves one at
 * a time — happens for the rows someone actually looks at rather than for an entire address book
 * up front. Emits a single time and then stops observing: the point is to trigger a fetch, not to
 * track visibility.
 *
 * The observer is created outside Angular's zone and the emit is brought back in, so scrolling a
 * long list does not schedule a change-detection pass per intersection callback.
 */
@Directive({
  selector: '[appVisibleOnce]'
})
export class VisibleOnceDirective implements OnInit, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);

  @Output() appVisibleOnce = new EventEmitter<void>();

  private observer: IntersectionObserver | null = null;

  ngOnInit(): void {
    // Older browsers without IntersectionObserver simply get the eager behaviour, which is correct
    // but chattier — better than no photos at all.
    if (typeof IntersectionObserver === 'undefined') {
      this.appVisibleOnce.emit();
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.disconnect();
            this.zone.run(() => this.appVisibleOnce.emit());
          }
        },
        // A little lead time so a photo is usually there by the time the row is properly on screen.
        { rootMargin: '120px' }
      );
      this.observer.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;
  }
}
